import React from 'react'
import { useMutation, type InfiniteData } from '@tanstack/react-query'
import { useLocalstorageState, useOutsideClick } from 'rooks'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import { Icon } from '@mobi/component-library/Common/V2/Icon'
import { Tooltip } from '@mobi/component-library/Common/V2/Tooltip'
import { triggerHapticFeedback } from '@core/Utils/hapticFeedback/hapticFeedback'
import {
  postFollowMeFollowAccount,
  postUnFollowMeFollowAccount,
  postFollowedAccountNotifications,
} from '@core/Areas/FollowMe/helpers/api'
import { queryClient } from '@core/Data/ReactQuery'
import { followMeFollowingProfilesQuery } from '@core/Areas/FollowMe/helpers/queries'
import {
  trackFollowMeNotificationToggle,
  trackFollowMeToggleFollow,
} from '@core/Areas/FollowMe/helpers/analytics'
import { handleApiErrorNotification } from '@core/Areas/FollowMe/helpers/handleApiErrorNotification'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import type { FollowMeSharedBet } from '@core/Areas/FollowMe/types'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { useLogon } from '@core/Utils/hooks'
import {
  getPushNotificationAlertStatus,
  togglePushNotificationAlertStatus,
} from '@core/Utils/notifications/pushNotificationAlerts'

export const FollowButton: React.FC<{
  accountId: string
  isFollowing: boolean
  isNotificationsEnabled: boolean
  shouldShowUnfollow?: boolean
}> = ({ accountId, isFollowing, isNotificationsEnabled, shouldShowUnfollow = false }) => {
  const notifyButtonRef = React.useRef<HTMLButtonElement>(null)

  const [isFollowingState, setIsFollowing] = React.useState(isFollowing)
  const [isNotificationsEnabledState, setIsNotificationsEnabled] =
    React.useState(isNotificationsEnabled)

  React.useEffect(() => setIsFollowing(isFollowing), [isFollowing])
  React.useEffect(() => setIsNotificationsEnabled(isNotificationsEnabled), [isNotificationsEnabled])

  const { accountNumber } = useLogon()

  const [hasTooltipBeenSeen, setHasTooltipBeenSeen] = useLocalstorageState<boolean | null>(
    'tooltip:follow-me-notify-new-tip-button',
    false
  )

  useOutsideClick(notifyButtonRef, () => setHasTooltipBeenSeen(true))

  const followMutation = useMutation({
    mutationFn: () =>
      isFollowingState
        ? postUnFollowMeFollowAccount(accountId)
        : postFollowMeFollowAccount(accountId),
    onSuccess() {
      const newState = !isFollowingState
      setIsFollowing(newState)
      triggerHapticFeedback('impact-light')
      patchFeedDataWithIsFollowingChange({ accountId, isFollowing: newState })
      queryClient.invalidateQueries(followMeFollowingProfilesQuery)
      trackFollowMeToggleFollow(newState ? 'enable' : 'disable')
    },
    onError(e) {
      handleApiErrorNotification(e)
    },
  })

  const notifyMutation = useMutation({
    mutationFn: () =>
      checkPushNotificationRegistration(accountId, isNotificationsEnabledState, accountNumber),
    onSuccess() {
      const newNotifState = !isNotificationsEnabledState
      setIsNotificationsEnabled(newNotifState)
      triggerHapticFeedback('impact-light')
      if (!hasTooltipBeenSeen) setHasTooltipBeenSeen(true)
      trackFollowMeNotificationToggle(!isNotificationsEnabledState ? 'enable' : 'disable')
      patchFeedDataWithNotificationChange({ accountId, isNotificationsEnabled: newNotifState })
    },
    onError(e) {
      handleApiErrorNotification(e)
    },
  })

  const handleFollowClick = () => {
    followMutation.mutate()
  }

  const handleNotifyClick = () => {
    notifyMutation.mutate()
  }

  if (!isFollowingState) {
    return (
      <Button
        size='sm'
        color='secondary_color'
        disabled={followMutation.isPending}
        onClick={handleFollowClick}
      >
        Follow
      </Button>
    )
  }

  return (
    <>
      {shouldShowUnfollow && (
        <Button
          size='sm'
          color='secondary_outline'
          disabled={followMutation.isPending}
          onClick={() => followMutation.mutate()}
        >
          Following <Icon name='LineCheck' size='1.5rem' />
        </Button>
      )}

      {isReactNativeApp ? (
        <Button
          size='sm'
          isIconOnlyButton
          ref={notifyButtonRef}
          color='secondary_color'
          disabled={notifyMutation.isPending}
          onClick={handleNotifyClick}
        >
          <Icon name={isNotificationsEnabledState ? 'SolidBellRinging01' : 'LineBellPlus'} />

          {!hasTooltipBeenSeen && !isNotificationsEnabledState ? (
            <Tooltip
              color='blue'
              isHidden={notifyMutation.isPending}
              shouldHideCrossIcon
              triggerElRef={notifyButtonRef}
              hasTooltipBeenSeen={() => false}
              tooltipId='follow-me-notify-new-tip-button'
              onDismissed={() => setHasTooltipBeenSeen(true)}
              shouldOnlyRenderSingleInstance={true}
            >
              <div>Get notified for</div>
              <div>every new tip</div>
            </Tooltip>
          ) : null}
        </Button>
      ) : null}
    </>
  )
}

// =============
// Local Helpers
// =============

function patchFeeds(
  updater: (
    old?: InfiniteData<FollowMeSharedBet[]>
  ) => InfiniteData<FollowMeSharedBet[]> | undefined
) {
  queryClient.setQueriesData<InfiniteData<FollowMeSharedBet[]>>(
    { queryKey: queryKeys.followMeSharedBetsFeedBase },
    updater
  )
}

function patchFeedDataWithIsFollowingChange({
  accountId,
  isFollowing,
}: {
  accountId: string
  isFollowing: boolean
}) {
  patchFeeds(old =>
    old
      ? {
          ...old,
          pages: old.pages.map(page =>
            page.map(bet =>
              bet.ownerAccountId === accountId
                ? { ...bet, isFollowing, isNotificationsEnabled: false }
                : bet
            )
          ),
        }
      : old
  )
}

function patchFeedDataWithNotificationChange({
  accountId,
  isNotificationsEnabled,
}: {
  accountId: string
  isNotificationsEnabled: boolean
}) {
  patchFeeds(old =>
    old
      ? {
          ...old,
          pages: old.pages.map(page =>
            page.map(bet =>
              bet.ownerAccountId === accountId ? { ...bet, isNotificationsEnabled } : bet
            )
          ),
        }
      : old
  )
}

async function checkPushNotificationRegistration(
  accountId: string,
  isNotificationsEnabled: boolean,
  accountNumber: number | null
) {
  const registered = await getPushNotificationAlertStatus(accountNumber, 'followme')

  if (!registered) {
    await togglePushNotificationAlertStatus({
      accountNumber,
      isEnabled: true,
      alertType: 'followme',
      deepLink: '',
    })
  }
  postFollowedAccountNotifications(accountId, !isNotificationsEnabled)
}
